import React, { useState, useRef, useCallback, useEffect } from 'react';
import Header from './Header';
import StatusBar from './StatusBar';
import type { ConnectionStatus } from './StatusBar';
import ChatContainer from './ChatContainer';
import CommandCenterPanel from './CommandCenterPanel';
import type { CommandCenterTab } from './CommandCenterPanel';
import EquabotSettings from './EquabotSettings';
import type { ExecutionStep, PendingAction } from '../services/api/agent/types';
import type { MessageSource } from '../services/gateway/types';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string | Date;
  source?: MessageSource;
}

interface Conversation {
  id: string;
  name: string;
  lastMessage?: string;
  updatedAt: string | Date;
}

interface EquanautSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  /** Rendering mode.
   * - `'overlay'` (default): fixed overlay panel that slides in from the right edge.
   * - `'rail'`: inline flex child for legacy route-owned panel containers.
   */
  mode?: 'overlay' | 'rail';
  messages?: Message[];
  conversations?: Conversation[];
  activeConversationId?: string;
  connectionStatus?: ConnectionStatus;
  isLoading?: boolean;
  error?: string | null;
  executingSteps?: ExecutionStep[];
  pendingAction?: PendingAction | null;
  commandCenterMode?: boolean;
  onToggleCommandCenter?: () => void;
  onExpandToFullPage?: () => void;
  initialCommandCenterTab?: CommandCenterTab;
  organizationId?: string;
  sessionKey?: string;
  onSendMessage?: (content: string, attachments?: File[]) => void;
  onSelectConversation?: (id: string) => void;
  onNewChat?: () => void;
  onImportFromDevice?: () => void;
  onRenameConversation?: (id: string, newName: string) => void;
  onDeleteConversation?: (id: string) => void;
  onFeedback?: (messageId: string, feedback: 'like' | 'dislike' | null) => void;
  onDismissError?: () => void;
  onConfirmAction?: (confirmed: boolean, confirmationCode?: string) => void;
}

const MIN_WIDTH = 360;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 420;

const CC_MIN_WIDTH = 800;
const noop = () => undefined;
const noopSendMessage = (_content: string, _attachments?: File[]) => undefined;
const noopSelectConversation = (_id: string) => undefined;

const EquanautSidebar: React.FC<EquanautSidebarProps> = ({
  isOpen,
  onClose,
  mode = 'overlay',
  messages = [],
  conversations = [],
  activeConversationId,
  connectionStatus = 'connected',
  isLoading = false,
  error = null,
  executingSteps = [],
  pendingAction = null,
  commandCenterMode = false,
  onToggleCommandCenter,
  onExpandToFullPage,
  initialCommandCenterTab,
  organizationId,
  sessionKey = 'agent:main:main',
  onSendMessage = noopSendMessage,
  onSelectConversation = noopSelectConversation,
  onNewChat = noop,
  onImportFromDevice,
  onRenameConversation,
  onDeleteConversation,
  onFeedback,
  onDismissError,
  onConfirmAction,
}) => {
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [settingsMode, setSettingsMode] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(DEFAULT_WIDTH);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
  }, [width]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const delta = startXRef.current - e.clientX;
      const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidthRef.current + delta));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleToggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const handleToggleSettings = () => {
    setSettingsMode(!settingsMode);
  };

  const effectiveWidth = commandCenterMode && !isMaximized
    ? Math.max(width, CC_MIN_WIDTH)
    : width;

  const sidebarStyle: React.CSSProperties = mode === 'rail' ? {
    // Rail mode: inline flex child for route-owned panel containers.
    position: 'relative',
    height: '100%',
    width: '100%',
    backgroundColor: '#2B3B44',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  } : {
    // Overlay mode (default): fixed panel that slides in from the right edge.
    position: 'fixed',
    top: 0,
    right: 0,
    height: '100vh',
    width: isMaximized ? '100vw' : `${effectiveWidth}px`,
    backgroundColor: '#2B3B44',
    boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 9998,
    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
    transition: isResizing ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
  };

  const resizeHandleStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '6px',
    height: '100%',
    cursor: 'col-resize',
    backgroundColor: isResizing ? '#33AA40' : 'transparent',
    transition: 'background-color 0.15s ease',
    zIndex: 10,
  };

  const resizeHandleHoverStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '1px',
    transform: 'translateY(-50%)',
    width: '4px',
    height: '40px',
    borderRadius: '2px',
    backgroundColor: 'rgba(255,255,255,0.2)',
  };

  const showConversationList = isMaximized || effectiveWidth >= 600;
  const showCCToggle = isMaximized || effectiveWidth >= 600;
  const showCCPanel = commandCenterMode && (isMaximized || effectiveWidth >= CC_MIN_WIDTH);

  const splitContainerStyle: React.CSSProperties = {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  };

  const chatPaneStyle: React.CSSProperties = {
    flex: showCCPanel ? '1 1 60%' : '1 1 100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
  };

  const ccPaneStyle: React.CSSProperties = {
    flex: '0 0 320px',
    maxWidth: '380px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  return (
    <div ref={sidebarRef} style={sidebarStyle}>
      {!isMaximized && (
        <div
          style={resizeHandleStyle}
          onMouseDown={handleMouseDown}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(51,170,64,0.3)';
          }}
          onMouseLeave={(e) => {
            if (!isResizing) {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
            }
          }}
        >
          <div style={resizeHandleHoverStyle} />
        </div>
      )}
      <Header
        isMaximized={isMaximized}
        onToggleMaximize={handleToggleMaximize}
        onClose={onClose}
        commandCenterMode={commandCenterMode}
        onToggleCommandCenter={onToggleCommandCenter}
        showCommandCenterToggle={showCCToggle}
        onExpandToFullPage={onExpandToFullPage}
        settingsMode={settingsMode}
        onToggleSettings={handleToggleSettings}
      />
      <StatusBar status={connectionStatus} />
      {settingsMode ? (
        <EquabotSettings onBack={() => setSettingsMode(false)} />
      ) : (
        <div style={splitContainerStyle}>
          <div style={chatPaneStyle}>
            <ChatContainer
              messages={messages}
              conversations={conversations}
              activeConversationId={activeConversationId}
              isLoading={isLoading}
              error={error}
              showConversationList={showConversationList && !showCCPanel}
              executingSteps={executingSteps}
              pendingAction={pendingAction}
              onSendMessage={onSendMessage}
              onSelectConversation={onSelectConversation}
              onNewChat={onNewChat}
              onImportFromDevice={onImportFromDevice}
              onRenameConversation={onRenameConversation}
              onDeleteConversation={onDeleteConversation}
              onFeedback={onFeedback}
              onDismissError={onDismissError}
              onConfirmAction={onConfirmAction}
            />
          </div>
          {showCCPanel && (
            <div style={ccPaneStyle}>
              <CommandCenterPanel
                organizationId={organizationId}
                sessionKey={sessionKey}
                gatewayConnected={connectionStatus === 'connected'}
                initialTab={initialCommandCenterTab}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EquanautSidebar;
